import theme from '@kiper/theme';
import { useEffect, useRef } from 'react';

const BINARY_CHARACTERS = '01';
const FONT_SIZE = 16;
const INITIAL_DROP_OFFSET = -25;
const RESET_PROBABILITY = 0.975;
const DEFAULT_SPEED = 0.8;

const useBinaryRain = ({
  color = theme.colors.blackShades(0.9),
  fadeOpacity = 0.05,
  speed = DEFAULT_SPEED,
} = {}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const dropsRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return () => {};

    const ctx = canvas.getContext('2d');
    const streams = BINARY_CHARACTERS.split('');

    const setupCanvas = () => {
      const parent = canvas.parentElement;
      if (!parent) return;

      const parentWidth = parent.offsetWidth;
      const parentHeight = parent.offsetHeight;

      canvas.width = parentWidth;
      canvas.height = parentHeight;

      const columnsCount = Math.floor(parentWidth / FONT_SIZE);
      const needsInitialization = dropsRef.current.length !== columnsCount;

      if (needsInitialization) {
        dropsRef.current = [];
        for (let i = 0; i < columnsCount; i++) {
          const randomOffset = Math.random() * INITIAL_DROP_OFFSET;
          dropsRef.current[i] = randomOffset;
        }
      }
    };

    const draw = () => {
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const fadeColor = `rgba(255, 255, 255, ${fadeOpacity})`;
      const fontStyle = `${FONT_SIZE}px`;
      const dropsCount = dropsRef.current.length;

      ctx.fillStyle = fadeColor;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      ctx.fillStyle = color;
      ctx.font = fontStyle;

      for (let i = 0; i < dropsCount; i++) {
        const randomIndex = Math.floor(Math.random() * streams.length);
        const character = streams[randomIndex];
        const xPosition = i * FONT_SIZE;
        const yPosition = dropsRef.current[i] * FONT_SIZE;

        ctx.fillText(character, xPosition, yPosition);

        const dropReachedBottom = yPosition > canvasHeight;
        const shouldReset = Math.random() > RESET_PROBABILITY;

        if (dropReachedBottom && shouldReset) {
          dropsRef.current[i] = 0;
        }

        dropsRef.current[i] += speed;
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    setupCanvas();
    animationRef.current = requestAnimationFrame(draw);

    const handleResize = () => {
      setupCanvas();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [color, fadeOpacity, speed]);

  return canvasRef;
};

export default useBinaryRain;
